<template>
  <iColumn gap="extraSmall" width="fill">
    <iText
      v-if="!preview && !canCommentOnPosts"
      variant="subtle"
      data-test="comment-input-off"
    >
      Comments are turned off.
    </iText>
    <iButton
      v-else-if="!preview && !storyComments.isReaderSignedIn"
      variant="secondary"
      width="fill"
      text-alignment="left"
      data-test="comment-input-sign-in"
      @click="storyComments.requireSignedInReader()"
    >
      Sign in to comment
    </iButton>
    <iCommentComposer
      v-else
      ref="composer"
      :key="composerKey"
      :on-submit="submitComment"
      :completions="completions"
      :disabled="disabled"
      :initial-segments="initialSegments"
      :input-style-overrides="composerInputStyle"
      :layout="layout"
      :placeholder="placeholder"
      :submit-label="submitLabel"
      :send-variant="COMMENT_SEND_VARIANT"
      :send-style-overrides="COMMENT_SEND_STYLE"
      :show-cancel="layout === STACKED"
      hide-cancel-when-empty
      @cancel="clear"
    >
      <template #leading>
        <slot name="leading" />
      </template>
    </iCommentComposer>
    <iText
      v-if="!preview && (thread.postError || replyError)"
      variant="error"
      font-size="small"
      data-test="comment-input-error"
    >
      {{ replyError || thread.postError }}
    </iText>
  </iColumn>
</template>

<script>
import { mapState } from "pinia";
import { emojiCompletion } from "@bloglovin/vue-component-library";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useStoryCommentsStore } from "%/dynamic-component-editor/stores/story-comments-store";
import { SentCommentNotEchoedError, mentionCompletionFor } from "%/dynamic-component-editor/helpers/story-comments.mjs";
import {
  COMMENT_INPUT_STYLE,
  COMMENT_SEND_STYLE,
  COMMENT_SEND_VARIANT,
} from "%/dynamic-component-editor/constants/comment-composer-style";

const STACKED = "stacked";

export default {
  name: "StoryCommentBox",
  props: {
    storyId: {
      type: Number,
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    inputStyleOverrides: {
      type: Object,
      default: () => ({}),
    },
    layout: {
      type: String,
      default: STACKED,
    },
    placeholder: {
      type: String,
      default: "Add a comment…",
    },
    submitLabel: {
      type: String,
      default: "Comment",
    },
    initialSegments: {
      type: Array,
      default: null,
    },
    replyTo: {
      type: Object,
      default: null,
    },
  },
  emits: ["replied"],
  data() {
    return {
      replyError: "",
      STACKED,
      COMMENT_SEND_STYLE,
      COMMENT_SEND_VARIANT,
    };
  },
  computed: {
    ...mapState(useConfigurationStore, ["canCommentOnPosts"]),
    storyComments() {
      return useStoryCommentsStore();
    },
    thread() {
      return this.storyComments.thread(this.storyId);
    },
    completions() {
      return [mentionCompletionFor(() => this.thread.comments), emojiCompletion];
    },
    composerInputStyle() {
      return { ...COMMENT_INPUT_STYLE, ...this.inputStyleOverrides };
    },
    composerKey() {
      return `${this.storyId}-${this.replyTo?.id ?? 0}`;
    },
  },
  methods: {
    focus() {
      this.$refs.composer?.focus();
    },
    clear() {
      this.$refs.composer?.clear();
      this.thread.postError = "";
      this.replyError = "";
    },
    submit() {
      return this.$refs.composer ? this.$refs.composer.submit() : Promise.resolve(false);
    },
    payload() {
      return this.$refs.composer ? this.$refs.composer.payload() : null;
    },
    submitComment(segments) {
      if (!this.replyTo) {
        return this.storyComments.postComment(this.storyId, segments).catch(error => {
          if (!(error instanceof SentCommentNotEchoedError)) {
            throw error;
          }
        });
      }
      this.replyError = "";
      return this.storyComments.postReply(this.storyId, this.replyTo, segments).then(reply => {
        this.$emit("replied", reply);
      }, error => {
        if (error instanceof SentCommentNotEchoedError) {
          this.$emit("replied", null);
          return;
        }
        this.replyError = error.message;
        throw error;
      });
    },
  },
};
</script>
